<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";

import Button from "~/components/dumb/Button.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import DropdownMenuWithSelection from "~/components/dumb/DropdownMenuWithSelection.vue";
import Input from "~/components/dumb/Input.vue";
import Modal from "~/components/dumb/Modal.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { copyAndNotify } from "~/components/notifications";
import { THROTTLE_MS } from "~/constants/app";
import { DotsHorizontalIcon, DuidFieldIcon, InfoIcon, PlusIcon, UsersRemoveIcon, XIcon } from "~/icons";
import { ButtonStyle, DropdownMenuItemKind, EventKind, IconSize, ModalWidth, Placement } from "~/shared/enums";
import type { AgentWorkflow, DropdownMenuSection, User } from "~/shared/types";
import { useDataStore, useEnvironmentStore } from "~/stores";
import { ThrottleManager } from "~/utils/throttleManager";
import { makeValidateHeaderKey, makeValidateWebhookUrl } from "~/utils/validation";

const POST_BODY_FORMAT_DESCRIPTION =
  "The format of the POST request body. Use {{data}} to include all workflow-specific data. Access specific details with a period, e.g. {{data.actor}} for the details of the user that triggered the workflow.";
const POST_BODY_EXAMPLE = `{
  "data": {{data}}
}`;
const TRIGGER_OPTIONS = [
  {
    eventKind: EventKind.TASK_UPDATE_ASSIGNEES,
    title: "Task assigned",
    description: (name: string) => `a task is assigned to ${name}`,
  },
  {
    eventKind: EventKind.TASK_COMMENT,
    title: "Comment sent",
    description: (name: string) => `a comment is sent to ${name}`,
  },
];

const props = defineProps<{
  agent: User | undefined;
}>();

const emit = defineEmits<{
  close: [];
}>();

const dataStore = useDataStore();
const environmentStore = useEnvironmentStore();

const selectedWorkflowIndex = ref(0);
const selectedWorkflow = computed(() => props.agent?.workflows?.[selectedWorkflowIndex.value]);

const getTriggerTitle = (trigger: EventKind) => TRIGGER_OPTIONS.find((e) => e.eventKind === trigger)?.title ?? "";

const contextMenuSections = (workflow: AgentWorkflow): DropdownMenuSection[] => [
  {
    title: "Actions",
    items: [
      {
        title: "Copy ID",
        kind: DropdownMenuItemKind.BUTTON,
        icon: DuidFieldIcon,
        onClick: () => copyAndNotify("Workflow ID", workflow.duid),
      },
      {
        title: "Delete",
        kind: DropdownMenuItemKind.BUTTON,
        icon: UsersRemoveIcon,
        onClick: () => {
          if (!props.agent) {
            return;
          }
          dataStore.deleteAgentWorkflow(props.agent.duid, workflow.duid);
        },
      },
    ],
  },
];

const triggerOptions = computed(() =>
  TRIGGER_OPTIONS.map(({ eventKind, description }) => ({
    title: description(props.agent?.name ?? ""),
    selected: selectedWorkflow.value?.trigger === eventKind,
    onClick: () => {
      if (!props.agent || !selectedWorkflow.value) {
        return;
      }
      dataStore.updateAgentWorkflow(props.agent.duid, {
        duid: selectedWorkflow.value.duid,
        trigger: eventKind,
      });
    },
  }))
);
const selectedTrigger = computed(() => triggerOptions.value.find((e) => e.selected)?.title);

const validateWebhookUrl = makeValidateWebhookUrl(environmentStore.urlBase);

const validateHeaderKeyWithDuplicates = (currentIndex: number) =>
  makeValidateHeaderKey(selectedWorkflow.value?.params.headers, currentIndex);

const createWorkflow = () => {
  if (!props.agent) {
    return;
  }
  dataStore.createAgentWorkflow(props.agent.duid);
  selectedWorkflowIndex.value = props.agent.workflows.length - 1;
};

const urlInputRef = ref<InstanceType<typeof Input>>();
const updateUrl = (url: string) => {
  if (!urlInputRef?.value?.isValid || !props.agent || !selectedWorkflow.value) {
    return;
  }
  dataStore.updateAgentWorkflow(props.agent.duid, {
    duid: selectedWorkflow.value.duid,
    params: { ...selectedWorkflow.value.params, url: url.trim() },
  });
};

const headerInputs = ref<InstanceType<typeof Input>[]>([]);
const removeEmptyHeaders = () => {
  if (!props.agent || !selectedWorkflow.value) {
    return;
  }
  const nonEmptyHeaders = selectedWorkflow.value.params.headers.filter(([key]) => key.trim() !== "");
  if (nonEmptyHeaders.length !== selectedWorkflow.value.params.headers.length) {
    dataStore.updateAgentWorkflow(props.agent.duid, {
      duid: selectedWorkflow.value.duid,
      params: {
        ...selectedWorkflow.value.params,
        headers: nonEmptyHeaders,
      },
    });
  }
};
const addHeaderRow = () => {
  if (!props.agent || !selectedWorkflow.value) {
    return;
  }
  const hasEmptyHeaderRow = selectedWorkflow.value.params.headers.some(
    ([key, value]) => key.trim() === "" && value.trim() === ""
  );

  if (hasEmptyHeaderRow) {
    const emptyRowIndex = selectedWorkflow.value.params.headers.findIndex(
      ([key, value]) => key.trim() === "" && value.trim() === ""
    );
    nextTick(() => {
      if (headerInputs.value[emptyRowIndex]) {
        headerInputs.value[emptyRowIndex].focus();
      }
    });
    return;
  }
  dataStore.updateAgentWorkflow(props.agent.duid, {
    duid: selectedWorkflow.value.duid,
    params: {
      ...selectedWorkflow.value.params,
      headers: [...selectedWorkflow.value.params.headers, ["", ""]],
    },
  });
  nextTick(() => {
    headerInputs.value[headerInputs.value.length - 1].focus();
  });
};
const removeHeaderRow = (index: number) => {
  if (!props.agent || !selectedWorkflow.value) {
    return;
  }
  dataStore.updateAgentWorkflow(props.agent.duid, {
    duid: selectedWorkflow.value.duid,
    params: {
      ...selectedWorkflow.value.params,
      headers: selectedWorkflow.value.params.headers.filter((_, i) => i !== index),
    },
  });
};
const updateHeaderKey = (index: number, key: string) => {
  if (!props.agent || !selectedWorkflow.value) {
    return;
  }
  const headers = key
    ? selectedWorkflow.value.params.headers.map(
        ([k, v], i) => (i === index ? [key, v] : [k, v]) satisfies [string, string]
      )
    : selectedWorkflow.value.params.headers.filter((_, i) => i !== index);

  if (!validateHeaderKeyWithDuplicates(index)(key).isValid) {
    return;
  }

  dataStore.updateAgentWorkflow(props.agent.duid, {
    duid: selectedWorkflow.value.duid,
    params: { ...selectedWorkflow.value.params, headers },
  });
};
const updateHeaderValue = (index: number, value: string) => {
  if (!props.agent || !selectedWorkflow.value) {
    return;
  }
  dataStore.updateAgentWorkflow(props.agent.duid, {
    duid: selectedWorkflow.value.duid,
    params: {
      ...selectedWorkflow.value.params,
      headers: selectedWorkflow.value.params.headers.map(([k, v], i) => (i === index ? [k, value] : [k, v])),
    },
  });
};

const body = ref<string | undefined>();
watch(
  () => selectedWorkflow.value?.params.body,
  (newBody) => {
    body.value = newBody;
  }
);
const bodyManager = new ThrottleManager((agentDuid: string, workflowDuid: string, params: AgentWorkflow["params"]) => {
  dataStore.updateAgentWorkflow(agentDuid, { duid: workflowDuid, params });
}, THROTTLE_MS);
watch(body, (newBody) => {
  if (!props.agent || !selectedWorkflow.value) {
    return;
  }
  bodyManager.run(props.agent.duid, selectedWorkflow.value.duid, {
    ...selectedWorkflow.value.params,
    body: newBody ?? "",
  });
});

watch(
  () => selectedWorkflow.value,
  (_, oldWorkflow) => {
    if (!oldWorkflow || !props.agent) {
      return;
    }
    if (!oldWorkflow.params.url) {
      dataStore.deleteAgentWorkflow(props.agent.duid, oldWorkflow.duid);
    }
  }
);

watch(
  () => props.agent,
  (newAgent, oldAgent) => {
    if (oldAgent) {
      oldAgent.workflows.forEach((workflow) => {
        if (workflow.params.url) {
          return;
        }
        dataStore.deleteAgentWorkflow(oldAgent.duid, workflow.duid);
      });
    }

    if (newAgent) {
      if (newAgent.workflows.length === 0) {
        createWorkflow();
      } else {
        selectedWorkflowIndex.value = 0;
      }
    }
  }
);

const close = () => {
  removeEmptyHeaders();
  emit("close");
};

onMounted(() => nextTick(removeEmptyHeaders));
</script>

<template>
  <Modal
    :entity="agent"
    title="Edit workflows"
    hide-title
    :width="ModalWidth.XL"
    custom-styles="!p-0 overflow-hidden h-[640px]"
    @close="close">
    <template #default="{ entity }">
      <div v-if="entity" class="flex h-full">
        <div class="w-56 overflow-y-auto border-r px-2.5 pb-6 pt-[18px] bg-std border-lt">
          <div class="mb-1 flex items-center justify-between">
            <span class="select-none px-2.5 text-xs font-semibold uppercase text-vlt">Workflows</span>
            <Tooltip text="Create a new workflow">
              <Button
                :btn-style="ButtonStyle.SECONDARY"
                :icon="PlusIcon"
                :icon-size="IconSize.S"
                borderless
                text-style="text-vlt"
                a11y-label="New workflow"
                class="!p-0.5"
                @click="createWorkflow" />
            </Tooltip>
          </div>
          <div
            v-for="(workflow, index) in entity.workflows"
            :key="workflow.duid"
            :class="[
              'flex cursor-pointer items-center whitespace-nowrap rounded px-2.5 py-1 text-sm text-md',
              selectedWorkflow?.duid === workflow.duid ? 'bg-lt focus-ring-lt hover:bg-md' : 'bg-std hover:bg-md',
            ]"
            @click="selectedWorkflowIndex = index"
            @keydown.enter="selectedWorkflowIndex = index">
            {{ getTriggerTitle(workflow.trigger) }}
          </div>
        </div>

        <div v-if="selectedWorkflow" class="flex h-full flex-1 flex-col overflow-y-auto px-10 py-6">
          <div class="mb-6 flex items-center justify-between">
            <span class="text-base font-medium text-md">
              {{ selectedWorkflow.params.url ? "Edit workflow" : "Create workflow" }}
            </span>
            <DropdownMenu
              :sections="contextMenuSections(selectedWorkflow)"
              :placement="Placement.BOTTOM_RIGHT"
              :distance="0">
              <div class="rounded text-lt hover:bg-lt">
                <span class="sr-only">Manage workflow</span>
                <DotsHorizontalIcon class="icon-md" />
              </div>
            </DropdownMenu>
          </div>

          <div class="mb-[23px] flex flex-col gap-1">
            <span class="text-sm text-lt">When</span>
            <DropdownMenuWithSelection is-settings-mode :options="triggerOptions" border block :width-pixels="320">
              <div class="select-none rounded px-3 py-2 text-left text-sm shadow-sm focus-ring-std hover:bg-lt">
                {{ selectedTrigger }}
              </div>
            </DropdownMenuWithSelection>
            <span class="text-sm text-lt">Then</span>
            <div class="grow select-none rounded-md border px-3 py-2 text-left text-sm shadow-sm text-md border-md">
              send a POST request
            </div>
          </div>

          <div class="flex flex-col gap-1">
            <span class="text-sm text-lt">To</span>
            <div>
              <Input
                ref="urlInputRef"
                :init-value="selectedWorkflow.params.url"
                placeholder="Add URL"
                label="To"
                hide-label
                :validate="validateWebhookUrl"
                input-classes="!text-sm rounded-md"
                @finalize="updateUrl" />
            </div>
          </div>

          <div class="mb-[21px] flex w-full flex-col gap-1">
            <div class="flex items-center justify-between">
              <span class="text-sm text-lt">Headers</span>
              <Tooltip text="Add header">
                <Button
                  :btn-style="ButtonStyle.SECONDARY"
                  :icon="PlusIcon"
                  :icon-size="IconSize.S"
                  borderless
                  text-style="text-vlt"
                  a11y-label="Add header"
                  class="!p-0.5"
                  @click="addHeaderRow" />
              </Tooltip>
            </div>
            <div class="flex flex-col">
              <div
                v-for="([key, value], index) in selectedWorkflow.params.headers"
                :key="index"
                class="group/header-row relative flex w-full">
                <Input
                  ref="headerInputs"
                  :init-value="key"
                  label="Header"
                  placeholder="Header"
                  hide-label
                  :validate="validateHeaderKeyWithDuplicates(index)"
                  hide-error
                  class="flex-1"
                  :input-classes="`!text-sm font-mono w-full !py-2 !px-3 rounded-none${index === 0 ? ' rounded-tl-md' : ''}${index === selectedWorkflow.params.headers.length - 1 ? ' rounded-bl-md' : ''}`"
                  @finalize="updateHeaderKey(index, $event)" />
                <Input
                  :init-value="value"
                  label="Value"
                  placeholder="Value"
                  hide-label
                  :input-classes="`!text-sm font-mono w-full !py-2 !px-3 rounded-none${index === 0 ? ' rounded-tr-md' : ''}${index === selectedWorkflow.params.headers.length - 1 ? ' rounded-br-md' : ''}`"
                  class="flex-1"
                  @finalize="updateHeaderValue(index, $event)" />
                <div class="absolute -top-1.5 right-0 hidden h-1.5 w-3 group-hover/header-row:flex" />
                <div class="absolute -right-1.5 top-0 hidden h-3 w-1.5 group-hover/header-row:flex" />
                <button
                  type="button"
                  class="absolute -right-1.5 -top-1.5 hidden cursor-pointer items-center justify-center rounded-full border bg-lt border-md icon-xs group-hover/header-row:flex"
                  aria-label="Remove default property"
                  @click.stop="removeHeaderRow(index)"
                  @keydown.enter="removeHeaderRow(index)">
                  <Tooltip text="Remove default property">
                    <XIcon class="size-full text-lt focus:outline-none" />
                  </Tooltip>
                </button>
              </div>
            </div>
          </div>

          <div class="flex flex-col gap-1">
            <div class="flex items-center gap-1">
              <span class="text-sm text-lt">Body</span>
              <Tooltip :text="POST_BODY_FORMAT_DESCRIPTION" :placement="Placement.RIGHT_TOP" info :skidding="-10">
                <InfoIcon class="cursor-help rounded-full outline-none bg-std text-vlt icon-xs" />
              </Tooltip>
            </div>
            <textarea
              v-model="selectedWorkflow.params.body"
              aria-label="Workflow"
              class="w-full rounded-md border px-3 py-2 font-mono text-sm bg-std text-md border-md focus-ring-none placeholder:text-vlt hover:border-hvy focus:border-primary-base dark:focus:border-primary-base"
              rows="6"
              :placeholder="POST_BODY_EXAMPLE" />
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>
